<!-- src/components/Ingredients/IngredientSearch.vue -->
<template>
  <div class="wrapper">
    <!-- Barre de recherche -->
    <div class="input-group" color="secondary">
      <v-text-field
        v-model="searchQuery"
        :label="$t('searchByName')"
        variant="outlined"
        clearable
        class="input-field"
        @keyup.enter="searchByName"
        hide-details
      />
      <v-btn
        color="accent"
        rounded="lg"
        class="input-button"
        @click="searchByName"
        :loading="isSearching"
      >
        <v-icon class="mr-1">mdi-magnify</v-icon>
        <span>Rechercher</span>
      </v-btn>
    </div>

    <!-- Résultats recherche par nom -->
    <div class="results-wrapper" v-if="searchResults.length">
      <v-btn
        class="scroll-btn scroll-btn-left"
        icon
        size="small"
        variant="elevated"
        :disabled="!canScrollLeft"
        @click="scrollResults(-1)"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div ref="resultsRef" class="results-group">
        <SearchResultCard
          v-for="(res, i) in searchResults"
          :key="'res-' + i"
          :product="res"
          :alreadyAdded="userIngredientsIds.includes(res.barcode)"
          @add="addSearchResult"
        />
      </div>
      <v-btn
        class="scroll-btn scroll-btn-right"
        icon
        size="small"
        variant="elevated"
        :disabled="!canScrollRight"
        @click="scrollResults(1)"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Message si aucun résultat -->
    <v-alert
      v-if="showNoResults"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      Aucun produit trouvé pour "{{ searchQuery }}".
    </v-alert>

    <!-- Snackbar pour les notifications -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      right
      timeout="3500"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import {
  getNovaStringAndColor,
  addIngredientToUserCollection,
  searchFoodByName,
  getUserIngredients,
} from "@/service/ingredient_service";
import { useAuthStore } from "@/store/auth_store";
import SearchResultCard from "./SearchResultCard.vue";

export default {
  name: "IngredientSearch",
  components: { SearchResultCard },
  emits: ['ingredient-added'],
  setup(props, { emit }) {
    const authStore = useAuthStore();

    const searchQuery = ref("");
    const searchResults = ref([]);
    const isSearching = ref(false);
    const userIngredientsIds = ref([]);
    const resultsRef = ref(null);
    const canScrollLeft = ref(false);
    const canScrollRight = ref(false);
    
    const snackbar = ref(false);
    const snackbarMessage = ref("");
    const snackbarColor = ref("success");
    const hasSearched = ref(false);

    const showNoResults = computed(() => 
      hasSearched.value && !isSearching.value && searchResults.value.length === 0 && searchQuery.value
    );

    const showSnack = (msg, color = "success") => {
      snackbarMessage.value = msg;
      snackbarColor.value = color;
      snackbar.value = true;
    };

    const extractNutrimentsFromJson = (nutriments) => {
      const nutrimentsArray = [];
      const importantKeys = [
        "energy-kcal",
        "fat",
        "saturated-fat",
        "sugars",
        "salt",
        "proteins",
        "carbohydrates",
        "fiber",
      ];
      for (const key in nutriments || {}) {
        if (key.endsWith("_100g")) {
          const name = key.replace("_100g", "");
          if (!importantKeys.includes(name)) continue;
          const value = nutriments[key];
          const unit = nutriments[`${name}_unit`] || "g";
          nutrimentsArray.push({ key: name, value: `${value} ${unit}` });
        }
      }
      return nutrimentsArray;
    };

    const getNutriscoreGrade = (source) => {
      if (!source) return "";
      const nutriscore = source.nutriscore;
      let grade = "";

      if (Array.isArray(nutriscore)) {
        const entry = nutriscore.find(
          (item) => item && typeof item === "object" && item.grade
        );
        grade = entry?.grade || "";
      } else if (nutriscore && typeof nutriscore === "object") {
        grade = nutriscore.grade || "";
      }

      if (!grade && source.nutriscore_grade) grade = source.nutriscore_grade;
      if (!grade && source.nutriscore_data?.grade) {
        grade = source.nutriscore_data.grade;
      }

      return grade || "";
    };

    const searchByName = async () => {
      if (!searchQuery.value) {
        showSnack("Veuillez entrer un nom de produit.", "warning");
        return;
      }
      isSearching.value = true;
      hasSearched.value = true;
      try {
        const results = await searchFoodByName(searchQuery.value);
        searchResults.value = results.map((p) => ({
          barcode: p.code,
          foodInfo: p,
          productImage: p.image_front_url || "https://via.placeholder.com/150",
          productBrand: p.brands || "Marque non disponible",
          productName:
            p.product_name_fr || p.product_name || "Nom non disponible",
          nutriscore: getNutriscoreGrade(p),
          novaGroup: getNovaStringAndColor(p.nova_group),
          labels: p.labels ? [p.labels] : [],
          nutriments_map: extractNutrimentsFromJson(p.nutriments || {}),
          servingQuantity: p.serving_quantity || null,
          servingSize: p.serving_size || null,
          allergens: (p.allergens_tags || []).map((tag) => tag.split(":")[1]),
          additives: (p.additives_tags || []).map((tag) => tag.split(":")[1]),
          favorite: false,
          category: "",
        }));
        
        if (!searchResults.value.length) {
          showSnack("Aucun produit trouvé.", "info");
        }
      } catch (e) {
        console.error(e);
        showSnack("Erreur lors de la recherche.", "error");
      } finally {
        isSearching.value = false;
      }
    };

    const addSearchResult = async (res) => {
      const userId = authStore?.userInfos?.userid || authStore?.userid;
      
      if (!userId) {
        showSnack("Utilisateur non authentifié.", "error");
        return;
      }
      try {
        const ingredientData = {
          productName: res.productName,
          productImage: res.productImage,
          productBrand: res.productBrand,
          nutriscore: res.nutriscore || "",
          novaGroup: res.novaGroup,
          labels: res.labels,
          nutriments_map: res.nutriments_map,
          servingQuantity: res.servingQuantity,
          servingSize: res.servingSize,
          allergens: res.allergens,
          additives: res.additives,
          favorite: false,
          userId: userId,
          added_dt: new Date().toISOString(),
          category: res.category || "",
        };
        const result = await addIngredientToUserCollection(
          userId,
          res.barcode,
          ingredientData
        );
        if (result.alreadyExists) {
          showSnack("Ce produit est déjà dans votre liste.", "info");
        } else {
          showSnack("Produit ajouté avec succès !", "success");
          // Émettre un événement pour informer le parent
          emit('ingredient-added', res);
          // Recharger la liste des ingrédients de l'utilisateur
          loadUserIngredients();
        }
      } catch (e) {
        console.error(e);
        showSnack("Erreur lors de l'ajout du produit.", "error");
      }
    };

    const loadUserIngredients = async () => {
      if (authStore?.userid) {
        try {
          const userData = await getUserIngredients(authStore.userid);
          if (userData && typeof userData === 'object') {
            userIngredientsIds.value = Object.keys(userData);
          }
        } catch (e) {
          console.error(
            "Erreur lors de la récupération des ingrédients utilisateur:",
            e
          );
        }
      }
    };

    const updateScrollButtons = () => {
      const el = resultsRef.value;
      if (!el) {
        canScrollLeft.value = false;
        canScrollRight.value = false;
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      canScrollLeft.value = el.scrollLeft > 4;
      canScrollRight.value = el.scrollLeft < maxScroll - 4;
    };

    const attachScrollListener = () => {
      const el = resultsRef.value;
      if (!el) return;
      el.removeEventListener("scroll", updateScrollButtons);
      el.addEventListener("scroll", updateScrollButtons, { passive: true });
      updateScrollButtons();
    };

    const scrollResults = (direction) => {
      const el = resultsRef.value;
      if (!el) return;
      const cards = Array.from(el.querySelectorAll(".result-card"));
      if (!cards.length) return;
      const center = el.scrollLeft + el.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      const nextIndex = Math.min(
        cards.length - 1,
        Math.max(0, closestIndex + direction)
      );
      cards[nextIndex].scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    };

    onMounted(() => {
      loadUserIngredients();
      attachScrollListener();
      window.addEventListener("resize", updateScrollButtons);
    });

    onBeforeUnmount(() => {
      const el = resultsRef.value;
      if (el) el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    });

    watch(searchResults, async () => {
      await nextTick();
      attachScrollListener();
    });

    return {
      searchQuery,
      searchResults,
      isSearching,
      userIngredientsIds,
      snackbar,
      snackbarMessage,
      snackbarColor,
      showNoResults,
      searchByName,
      addSearchResult,
      resultsRef,
      scrollResults,
      canScrollLeft,
      canScrollRight,
    };
  },
};
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  max-width: 100%;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(var(--v-theme-text), 0.1);
  background: rgba(var(--v-theme-secondary), 0.9);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.18);
}

@media (min-width: 960px) {
    .input-group {
        flex-direction: row;
    }
}

.input-field {
  width: 100%;
}

@media (min-width: 600px) {
    .input-field {
        width: 400px;
    }
}

.input-button {
  min-width: 150px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.results-wrapper {
  position: relative;
  --card-width: clamp(260px, 78vw, 320px);
  --carousel-pad: clamp(16px, 3vw, 32px);
  border-radius: 22px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: rgba(var(--v-theme-secondary), 0.85);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.results-group {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: 20px;
  padding: 14px var(--carousel-pad) 18px;
  margin: 0;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: var(--carousel-pad);
  scroll-behavior: smooth;
  scrollbar-gutter: stable both-edges;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  background: transparent;
}

.results-group::-webkit-scrollbar {
  display: none;
}

.results-group :deep(.result-card) {
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgb(var(--v-theme-accent));
  border: 1px solid rgba(var(--v-theme-text), 0.22);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(8px);
  color: rgb(var(--v-theme-primary));
}

.scroll-btn-left {
  left: 6px;
}

.scroll-btn-right {
  right: 6px;
}

.scroll-btn:disabled {
  opacity: 0.35;
  filter: grayscale(0.6);
}
</style>
